/**
 * Theoretical customer scenarios for the pricing simulator.
 *
 * The real-organization view can only answer "what do the customers we happen to
 * have today earn us". A scenario answers the question that actually drives
 * pricing decisions: "what would 40 customers who look like THIS earn us". A
 * scenario is a list of customer profiles — each a usage shape plus how many
 * customers have it — run through the exact same pricing levers as the real view.
 *
 * Pure data + pure helpers, plus localStorage persistence so a scenario the
 * operator built survives a refresh.
 */

import type { OrgUsage } from './economics';

/** One theoretical customer shape, and how many customers look like it. */
export interface CustomerProfile {
  id: string;
  /** What this kind of customer is called (e.g. "Typical", "Heavy user"). */
  name: string;
  /** How many customers have this profile. */
  count: number;
  /** Reference stat only — not priced, not costed. */
  visitCount: number;
  /** Monthly SMS segments (up to 3 fold into one billed text). */
  smsSegments: number;
  /** Monthly picture messages (MMS). */
  mmsSegments: number;
  /** Monthly AI actions. */
  aiRequests: number;
  /** Monthly dollars collected by card. */
  cardVolume: number;
  /** Monthly card transactions. */
  cardTxns: number;
  /** Monthly dollars collected by bank (ACH). */
  achVolume: number;
  /** Monthly bank (ACH) transactions. */
  achTxns: number;
}

/**
 * The scenario the page opens with — three shapes drawn from what we actually
 * see today: a small operator, a typical one, and a heavy one modeled on
 * Quality Seasons (~4,900 text segments/mo). Every profile takes payments, so
 * each pays the Ruta Pay price; zero its card and bank volume to see the
 * higher one. Bank
 * share rises with customer size, since bigger invoices are where ACH pays off.
 */
export const DEFAULT_PROFILES: CustomerProfile[] = [
  {
    id: 'profile-small',
    name: 'Small operator',
    count: 10,
    visitCount: 300,
    smsSegments: 600,
    mmsSegments: 20,
    aiRequests: 120,
    cardVolume: 45_000,
    cardTxns: 280,
    achVolume: 5_000,
    achTxns: 20,
  },
  {
    id: 'profile-typical',
    name: 'Typical',
    count: 25,
    visitCount: 900,
    smsSegments: 1_800,
    mmsSegments: 60,
    aiRequests: 380,
    cardVolume: 140_000,
    cardTxns: 780,
    achVolume: 30_000,
    achTxns: 120,
  },
  {
    id: 'profile-heavy',
    name: 'Heavy user',
    count: 5,
    visitCount: 2_200,
    smsSegments: 4_900,
    mmsSegments: 150,
    aiRequests: 900,
    cardVolume: 420_000,
    cardTxns: 1_800,
    achVolume: 115_000,
    achTxns: 400,
  },
];

/** A blank profile to start from when the operator adds one. */
export function emptyProfile(id: string): CustomerProfile {
  return {
    id,
    name: 'New profile',
    count: 1,
    visitCount: 0,
    smsSegments: 0,
    mmsSegments: 0,
    aiRequests: 0,
    cardVolume: 0,
    cardTxns: 0,
    achVolume: 0,
    achTxns: 0,
  };
}

/** Total simulated customers — what fixed platform overhead gets spread across. */
export function scenarioCustomerCount(profiles: CustomerProfile[]): number {
  return profiles.reduce((sum, p) => sum + Math.max(0, p.count), 0);
}

/** One profile's usage, as the economics model consumes it (per SINGLE customer). */
export function profileToUsage(p: CustomerProfile): OrgUsage {
  return {
    laborHours: 0,
    visitCount: p.visitCount,
    smsSegments: p.smsSegments,
    mmsSegments: p.mmsSegments,
    aiRequests: p.aiRequests,
    cardVolume: p.cardVolume,
    cardTxns: p.cardTxns,
    achVolume: p.achVolume,
    achTxns: p.achTxns,
  };
}

const STORAGE_KEY = 'ruta-admin:pricing-scenario:v1';

/**
 * Load the saved scenario, falling back to the defaults. Anything malformed in
 * storage is discarded rather than crashing the page — a bad blob is not worth a
 * white screen on an internal tool.
 */
export function loadScenario(): CustomerProfile[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PROFILES;

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_PROFILES;

    const profiles = parsed.filter(isCustomerProfile);
    return profiles.length > 0 ? profiles : DEFAULT_PROFILES;
  } catch {
    return DEFAULT_PROFILES;
  }
}

export function saveScenario(profiles: CustomerProfile[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
  } catch {
    // Storage full or blocked — the scenario just won't persist. Not fatal.
  }
}

export function clearScenario(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Same as above — nothing to recover from.
  }
}

const NUMERIC_KEYS = [
  'count',
  'visitCount',
  'smsSegments',
  'mmsSegments',
  'aiRequests',
  'cardVolume',
  'cardTxns',
  'achVolume',
  'achTxns',
] as const;

function isCustomerProfile(value: unknown): value is CustomerProfile {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Record<string, unknown>;
  if (typeof p.id !== 'string' || typeof p.name !== 'string') return false;
  return NUMERIC_KEYS.every((key) => typeof p[key] === 'number' && Number.isFinite(p[key]));
}
